const cartItems = class extends HTMLElement {
  constructor () {
    super()
    this.parser = new DOMParser() // eslint-disable-line
    window.addEventListener('cartAPI', ({ detail: { cartData } }) => {
      this.buildList(cartData)
    })
  }

  async buildList (cartData) {
    if (!cartData.items || cartData.items.length === 0) { return }
    const newItems = cartData.items
      .filter(item => this.querySelector(`[data-cart-item][data-key="${item.key}"]`) === null)

    const softBundlesAdded = []

    for (const item of newItems) {
      const childBundleProducts = checkBundleChildren(item, cartData)
      const isBundleChildProduct = typeof item.properties?._child_bundle_product !== 'undefined'
      const isSoftBundleProduct = typeof item.properties?._child_soft_bundle !== 'undefined'
      if (isBundleChildProduct) { continue }

      const elmLit = (isSoftBundleProduct && softBundlesAdded.includes(item.properties?._child_soft_bundle))
        ? cartItemTemplateEmpty(item, childBundleProducts)
        : await cartItemTemplate(item, childBundleProducts, isSoftBundleProduct)

      const doc = this.parser.parseFromString(elmLit, 'text/html')
      const elm = doc.querySelector('cart-item')
      if (!elm) { continue }
      this.prepend(elm)

      if (isSoftBundleProduct) { softBundlesAdded.push(item.properties?._child_soft_bundle) }
    }

    document.dispatchEvent(new CustomEvent('cart:afterrender'))
  }
}

if (typeof window.customElements.get('cart-items') === 'undefined') {
  window.customElements.define('cart-items', cartItems)
}

const cartItem = class extends HTMLElement {
  constructor () {
    super()
    this.key = this.getAttribute('data-key')
    this.id = parseInt(this.getAttribute('data-id'), 10)
    this.itemData = {}
    this.variant = null
    this.bundleChildren = []
    this.isSoftBundle = this.hasAttribute('data-cart-item-soft-bundle')

    this.setItemData(cartAPI.getCartData())
    this.initQuantitySelector()
    this.initSellingPlanSelect()
    this.initRemoveButton()
    this.initSaveForLaterButton()

    window.addEventListener('cartAPI', ({ detail: { cartData } }) => {
      this.setItemData(cartData)
    })
  }

  async setItemData (cartData) {
    this.itemData = cartData.items.find(item => item.key === this.key)

    if (typeof this.itemData === 'undefined' || !this.itemData) {
      this.remove()
    } else {
      if (!this.variant && this.itemData.handle) {
        try {
          const res = await fetch(`/products/${encodeURIComponent(this.itemData.handle)}.js`)
          if (res.ok) {
            const product = await res.json()
            this.variant = product.variants.find(variant => variant.id === this.itemData.variant_id) || null
          }
        } catch (err) {
          console.error('Unable to load cart item product', err)
        }
      }

      this.bundleChildren = checkBundleChildren(this.itemData, cartData)
      this.updateCartItem()
    }
  }

  initQuantitySelector () {
    const quantitySelector = this.querySelector('[data-quantity-input]')
    if (!quantitySelector) { return }

    let changeTimeout = null
    quantitySelector.addEventListener('change', () => {
      if (changeTimeout !== null) { clearTimeout(changeTimeout) }
      changeTimeout = setTimeout(() => {
        this.quantityChange(quantitySelector.value)
      }, 250)
    })
  }

  initSellingPlanSelect () {
    const sellingPlanSelect = this.querySelector('[data-selling-plan-select]')
    if (!sellingPlanSelect) { return }

    sellingPlanSelect.addEventListener('change', async () => {
      if (!this.bundleChildren || this.bundleChildren.length === 0) {
        await cartAPI.cartChange({
          id: this.key,
          selling_plan: sellingPlanSelect.value
        })
      } else {
        this.swapBundleSellingPlans(sellingPlanSelect)
      }
    })
  }

  swapBundleSellingPlans (sellingPlanSelect) {
    const itemsToSwap = this.isSoftBundle
      ? []
      : [{
          oldItemKey: this.key,
          newItem: {
            id: this.itemData.id,
            quantity: this.itemData.quantity,
            selling_plan: sellingPlanSelect.value || null,
            properties: this.itemData.properties
          }
        }]

    const sellingPlanName = sellingPlanSelect
      .options[sellingPlanSelect.selectedIndex].text

    this.bundleChildren.forEach((childItem) => {
      try {
        const chosenPlan = JSON.parse(
          childItem.properties?._selling_plan_options.replace(/`/g, '"')
        ).find(option => option.sellingPlan.name === sellingPlanName)

        const chosenPlanID = typeof chosenPlan === 'undefined'
          ? null
          : chosenPlan.sellingPlan.id.split('SellingPlan/')[1]

        itemsToSwap.push({
          oldItemKey: childItem.key,
          newItem: {
            id: childItem.id,
            quantity: childItem.quantity,
            selling_plan: chosenPlanID,
            properties: childItem.properties
          }
        })
      } catch (err) {
        console.error(err)
        return null
      }
    })

    cartAPI.cartSwap(itemsToSwap)
  }

  initRemoveButton () {
    const removeButton = this.querySelector('[data-remove-cart-item]')
    if (!removeButton) { return }

    removeButton.addEventListener('click', () => {
      this.quantityChange(0)
      this.classList.add('removed')
      setTimeout(() => { this.remove() }, 350)
    })
  }

  initSaveForLaterButton () {
    this.saveForLaterButton = this.querySelector('[data-save-for-later]')
    if (!this.saveForLaterButton) { return }

    this.setSaveForLaterReady()
    document.addEventListener('swym:sfl-ready', () => { this.setSaveForLaterReady() }, { once: true })

    this.saveForLaterButton.addEventListener('click', () => { this.saveForLater() })
  }

  // The button stays hidden until Swym has handed us a Save For Later list id,
  // otherwise clicking it would silently do nothing.
  setSaveForLaterReady () {
    if (!this.saveForLaterButton) { return }
    this.saveForLaterButton.classList.toggle('is-ready', Boolean(window._swat?.sflLid))
  }

  saveForLater () {
    const swat = window._swat
    const lines = saveForLaterLines(this.itemData, this.bundleChildren)
    const products = lines.map(saveForLaterProduct).filter(Boolean)

    if (!swat?.SaveForLater || !swat.sflLid || products.length === 0) {
      console.warn('Swym Save For Later is not ready', {
        api: Boolean(swat?.SaveForLater),
        listId: swat?.sflLid,
        itemData: Boolean(this.itemData),
        products
      })
      return
    }

    this.saveForLaterButton.disabled = true

    // Swym validates epi/empi/du synchronously and throws when any are missing
    // or when du is not an absolute URL. du must be the canonical product URL,
    // without a variant query, or some variants are rejected.
    try {
      swat.SaveForLater.add(
        swat.sflLid,
        products,
        (response) => {
          const failedEpis = new Set(
            (response?.itemsfailed || []).map(item => Number(item.epi))
          )
          const savedLines = lines.filter(line => !failedEpis.has(Number(line.variant_id)))

          if (savedLines.length === 0) {
            console.error('Swym Save For Later add failed', response)
            this.saveForLaterButton.disabled = false
            return
          }

          if (savedLines.length === lines.length) {
            this.quantityChange(0)
          } else {
            const updates = { updates: {} }
            savedLines.forEach(line => { updates.updates[line.key] = 0 })
            cartAPI.cartUpdate(updates)
          }

          if (!savedLines.some(line => line.key === this.key)) {
            this.saveForLaterButton.disabled = false
            return
          }

          this.saveForLaterButton.classList.add('is-saved')
          this.classList.add('removed')
          setTimeout(() => { this.remove() }, 350)
        },
        (err) => {
          console.error('Swym Save For Later add failed', err)
          this.saveForLaterButton.disabled = false
        }
      )
    } catch (err) {
      console.error('Swym Save For Later add failed', err)
      this.saveForLaterButton.disabled = false
    }
  }

  async quantityChange (newQuantity) {
    newQuantity = parseInt(newQuantity, 10)

    if (!this.bundleChildren || this.bundleChildren.length === 0) {
      await cartAPI.cartChange({
        id: this.key,
        quantity: newQuantity
      })

      return
    }

    const itemsToUpdate = { updates: {} }
    itemsToUpdate.updates[this.key] = newQuantity

    this.bundleChildren.forEach((childItem) => {
      itemsToUpdate.updates[childItem.key] = newQuantity
    })

    cartAPI.cartUpdate(itemsToUpdate)
  }

  updateCartItem () {
    const priceDisplay = this.querySelector('[data-price]')
    if (priceDisplay !== null && (this.isSoftBundle || this.variant)) {
      const compareAtPrice = this.variant ? (this.variant.compare_at_price || this.variant.price) : 0
      priceDisplay.innerHTML = this.isSoftBundle
        ? bundleChildrenPrice(this.itemData, this.bundleChildren)
        : cartItemPrice(this.itemData.final_price, compareAtPrice)
      priceDisplay.classList.toggle('cart-item__price--sale', !this.isSoftBundle && compareAtPrice > this.itemData.final_price)
    }

    const quantitySelector = this.querySelector('[data-quantity-input]')
    if (quantitySelector !== null) { quantitySelector.value = this.itemData.quantity }

    const bundleChildrenHolder = this.querySelector('[data-bundle-childen]')
    if (bundleChildrenHolder !== null) {
      const isOpen = bundleChildrenHolder.querySelector('[data-bundle-details]').hasAttribute('open')
      bundleChildrenHolder.innerHTML = bundleChildrenDetails(this.itemData, this.bundleChildren)
      if (isOpen) { bundleChildrenHolder.querySelector('[data-bundle-details]').setAttribute('open', true) }
    }
  }
}

if (typeof window.customElements.get('cart-item') === 'undefined') {
  window.customElements.define('cart-item', cartItem)
}

// Swym matches du against the product's canonical URL. Cart line URLs include
// ?variant=, and that form is rejected for some variants.
const canonicalProductUrl = (item) => {
  if (item?.handle) {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/')
    return new URL(`${root}products/${item.handle}`, window.location.origin).href
  }

  if (!item?.url) return ''

  try {
    const url = new URL(item.url, window.location.origin)
    url.search = ''
    url.hash = ''
    return url.href
  } catch (err) {
    return ''
  }
}

const saveForLaterProduct = (item) => {
  const empi = Number(item?.product_id)
  const epi = Number(item?.variant_id)
  const du = canonicalProductUrl(item)
  if (!empi || !epi || !du) return null

  return {
    du,
    empi,
    epi,
    qty: Number(item.quantity) || 1
  }
}

const saveForLaterLines = (item, bundleChildren) => {
  const lines = new Map()
  ;[item, ...(bundleChildren || [])].forEach(line => {
    if (line?.key) lines.set(line.key, line)
  })
  return [...lines.values()]
}

const checkBundleChildren = (item, cartData) => {
  return cartData.items.filter((childItem) => {
    return (
      typeof item.properties !== 'undefined' &&
      typeof childItem.properties !== 'undefined' &&
      item.properties !== null &&
      childItem.properties !== null &&
      (
        (
          typeof item?.properties?._parent_bundle_product !== 'undefined' &&
          typeof childItem.properties._child_bundle_product !== 'undefined' &&
          item.properties._parent_bundle_product === childItem.properties._child_bundle_product
        ) ||
        (
          typeof item.properties._child_soft_bundle !== 'undefined' &&
          typeof childItem.properties._child_soft_bundle !== 'undefined' &&
          item.properties._child_soft_bundle === childItem.properties._child_soft_bundle
        )
      )
    )
  })
}
